import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { api, ApiError } from '@/shell/api/client'
import { afterSignIn, LANDING } from '@/shell/landing'
import { AuthLayout, FormError } from './auth-layout'
import { GoogleButton, googleErrorMessage, MethodDivider, useAuthMethods } from './google-button'

const Credentials = z.object({ email: z.string().email('Enter your work email'), password: z.string().min(1, 'Enter your password') })
const Code = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the six-digit code from your authenticator app') })
type Credentials = z.infer<typeof Credentials>
type Code = z.infer<typeof Code>

const PORTAL_REASONS: Record<string, string> = {
  no_seat: 'This email has no portal access. Ask your accountant for an invite.',
  principal_disabled: 'Your portal access was removed. Contact your accountant if this is unexpected.',
  seat_deactivated: 'Your portal access was removed. Contact your accountant if this is unexpected.',
}

const REASONS: Record<string, string> = {
  session_ended: 'Your session ended. Sign in again to continue.',
  signed_out: 'You signed out.',
  seat_deactivated: 'Your seat was deactivated. Ask your CFO if this is unexpected.',
  no_seat: 'This account has no seat for you. Ask your CFO for an invite.',
  principal_disabled: 'This account is disabled.',
  role_changed: 'Your role changed, so you were signed out. Sign in to continue with your new role.',
}

/**
 * PRD-019 §3.5: email + password, then a TOTP code every time. The wrong-password and locked answers
 * are identical by design, so the copy never says which. `from` restores the deep link (PRD-018 §3.10).
 *
 * PRD-019 Q1: "Continue with Google" sits under the form when the API offers it. Google sends the
 * browser back to /signin/totp (code step armed by the API) or to /signin?error=… when refused.
 */
export function SignInPage({ stage: initialStage = 'credentials', surface = 'firm' }: { stage?: 'credentials' | 'code', surface?: 'firm' | 'portal' } = {}) {
  // PRD-023 §3.5: the portal shares the sign-in floor (email + password + code) with its own copy and landing; no Google, no self-registration.
  const portal = surface === 'portal'
  const authPath = portal ? '/portal/auth' : '/auth'
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const methods = useAuthMethods()
  const state = (location.state ?? {}) as { from?: string, reason?: string }
  const [stage, setStage] = useState<'credentials' | 'code' | 'backup'>(initialStage)
  const [error, setError] = useState<string | null>(null)
  const googleError = googleErrorMessage(params.get('error'), methods.data?.googleDomains)

  const creds = useForm<Credentials>({ resolver: zodResolver(Credentials), defaultValues: { email: '', password: '' } })
  const code = useForm<Code>({ resolver: zodResolver(Code), defaultValues: { code: '' } })
  const backup = useForm<{ code: string }>({ defaultValues: { code: '' } })

  // PRD-018 §3.5 (Q1): a bounced deep link wins, otherwise Home.
  const finish = () => navigate(portal ? '/p' : afterSignIn(state.from), { replace: true })

  const onCredentials = creds.handleSubmit(async (v) => {
    setError(null)
    try {
      const res = await api<{ twoFactorRedirect?: boolean }>(`${authPath}/sign-in/email`, { method: 'POST', json: { email: v.email, password: v.password } })
      if (res.twoFactorRedirect)
        setStage('code')
      else finish()
    }
    catch (e) {
      setError(e instanceof ApiError && e.status === 401 ? 'Invalid email or password.' : 'Could not sign in. Try again.')
    }
  })

  const onCode = code.handleSubmit(async (v) => {
    setError(null)
    try {
      await api(`${authPath}/two-factor/verify-totp`, { method: 'POST', json: { code: v.code } })
      finish()
    }
    catch (e) {
      setError(e instanceof ApiError && e.status === 429 ? 'Too many attempts. Start over.' : 'That code did not work. Codes change every 30 seconds.')
      code.reset()
    }
  })

  const onBackup = backup.handleSubmit(async (v) => {
    setError(null)
    try {
      await api(`${authPath}/two-factor/verify-backup-code`, { method: 'POST', json: { code: v.code.trim() } })
      // Edge case 11: a backup code signs in once; every page now asks for a new enrollment.
      navigate(portal ? '/p/mfa/enroll' : '/mfa/enroll', { replace: true, state: { reason: 'backup_code_used' } })
    }
    catch {
      setError('That backup code did not work.')
    }
  })

  if (stage === 'credentials') {
    return (
      <AuthLayout title={portal ? 'Sign in to your portal' : 'Sign in'} lede={state.reason ? (portal ? PORTAL_REASONS[state.reason] ?? REASONS[state.reason] : REASONS[state.reason]) ?? null : null} footer={portal ? 'Forgot your password or lost your authenticator? Contact your accountant; they can send you a new invite.' : 'Forgot your password? Password reset by email is not wired yet; ask your CFO to send you a new invite.'}>
        <form onSubmit={onCredentials} className="flex flex-col gap-4" noValidate>
          <Input label={portal ? 'Email' : 'Work email'} type="email" autoComplete="username" isRequired hideRequiredIndicator name="email" value={creds.watch('email')} onChange={v => creds.setValue('email', v)} isInvalid={!!creds.formState.errors.email} hint={creds.formState.errors.email?.message} />
          <Input label="Password" type="password" autoComplete="current-password" isRequired hideRequiredIndicator name="password" value={creds.watch('password')} onChange={v => creds.setValue('password', v)} isInvalid={!!creds.formState.errors.password} hint={creds.formState.errors.password?.message} />
          <FormError message={error ?? googleError} />
          <Button type="submit" size="lg" isLoading={creds.formState.isSubmitting}>Continue</Button>
        </form>
        {methods.data?.google && !portal
          ? (
              <div className="mt-4 flex flex-col gap-4">
                <MethodDivider />
                <GoogleButton callbackPath={LANDING} errorPath="/signin" onError={setError} />
                <p className="text-center text-xs text-quaternary">Google signs you in; your authenticator code is still required.</p>
              </div>
            )
          : null}
      </AuthLayout>
    )
  }
  if (stage === 'code') {
    return (
      <AuthLayout title="Enter your code" lede="Open your authenticator app and enter the six-digit code for Gallop.">
        <form onSubmit={onCode} className="flex flex-col gap-4" noValidate>
          <Input label="Authentication code" inputMode="numeric" autoComplete="one-time-code" name="code" autoFocus value={code.watch('code')} onChange={v => code.setValue('code', v.replace(/\D/g, '').slice(0, 6))} isInvalid={!!code.formState.errors.code} hint={code.formState.errors.code?.message} />
          <FormError message={error} />
          <Button type="submit" size="lg" isLoading={code.formState.isSubmitting}>Sign in</Button>
          <Button
            type="button"
            color="link-gray"
            size="sm"
            onClick={() => {
              setError(null)
              setStage('backup')
            }}
          >
            Use a backup code instead
          </Button>
        </form>
      </AuthLayout>
    )
  }
  return (
    <AuthLayout title="Use a backup code" lede="Enter one of the backup codes you saved at enrollment. It works once, and you will set up your authenticator again right after.">
      <form onSubmit={onBackup} className="flex flex-col gap-4" noValidate>
        <Input label="Backup code" name="code" autoFocus autoComplete="off" value={backup.watch('code')} onChange={v => backup.setValue('code', v)} />
        <FormError message={error} />
        <Button type="submit" size="lg" isLoading={backup.formState.isSubmitting}>Sign in</Button>
        <Button
          type="button"
          color="link-gray"
          size="sm"
          onClick={() => {
            setError(null)
            setStage('code')
          }}
        >
          Back to authenticator code
        </Button>
      </form>
    </AuthLayout>
  )
}
